import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { AnimatePresence, motion } from "motion/react";
import { type ReactElement, useState } from "react";

import {
    submitUseCaseAndIntent,
    type UseCaseAndIntentPayload
} from "@/services/clientOnboarding";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useUseCaseFormState } from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { FormPrimaryAction } from "../panel/FormPrimaryAction";
import { SegmentedPicker } from "../panel/SegmentedPicker";
import type { NextStepTarget } from "../types";

import {
    CAPITAL_RANGE_TO_BACKEND,
    type CapitalRange,
    DISTRIBUTION_TO_BACKEND,
    type DistributionModel,
    setsEqual,
    USE_CASE_TO_BACKEND,
    type UseCaseId
} from "./useCaseFormShared";

export {
    INITIAL_USE_CASE_STATE,
    hasUseCaseFormDraft,
    type UseCaseFormState
} from "./useCaseFormShared";

const USE_CASE_OPTIONS: Array<{ value: UseCaseId; label: string }> = [
    { value: "treasury", label: "Treasury" },
    { value: "lp", label: "Liquidity Provider" },
    { value: "pol", label: "Protocol-Owned Liquidity" },
    { value: "neobank-d2u", label: "Neobank" },
    { value: "other", label: "Other" }
];

const CAPITAL_RANGE_OPTIONS: Array<{ value: CapitalRange; label: string }> = [
    { value: "<500k", label: "<$500k" },
    { value: "500k-2m", label: "$500k–$2M" },
    { value: "2m-10m", label: "$2M–$10M" },
    { value: "10m+", label: "$10M+" }
];

/**
 * Framed by outcome, not mechanism: what the yield is *for*, rather than
 * the route it takes to get there.
 *
 * `programmatic` (BE `protocol_distributes`) is deliberately absent. It
 * reaches the same outcome as `pass-through` by a different route, and
 * that distinction is not one the applicant is being asked to make here.
 * It stays in `DistributionModel` and in both backend maps so a server
 * value of `protocol_distributes` still hydrates — the FE just no longer
 * offers it as a choice.
 */
const DISTRIBUTION_OPTIONS: Array<{
    value: DistributionModel;
    label: string;
}> = [
    { value: "pass-through", label: "Distribute to Your Users" },
    { value: "retained", label: "Increase Your Revenue" }
];

/**
 * Use Case & Intent — three structured pickers, no document uploads.
 * The applicant declares what they're using YaaS for, expected capital,
 * and distribution model. Drives downstream proof applicability.
 *
 * Save / Saved cycle:
 *   - Initial: button reads "Save", disabled until selections are
 *     valid. Hitting it commits the current state.
 *   - Post-save, no edits: button reads "Saved" and is disabled
 *     (no-op affordance, just the committed-state indicator).
 *   - Post-save, edits made: button flips back to "Save" and re-enables
 *     when the new selections are valid. The subsection stays in the
 *     Complete status throughout — the button is the only thing that
 *     toggles between "Save" and "Saved" depending on whether the
 *     form is dirty.
 */
export function UseCaseForm({
    onSubmit,
    nextStep,
    onContinue
}: {
    onSubmit: (payload: unknown) => void;
    /** Forward-CTA target. When non-null and the form is in a saved-
     *  clean state, the bottom button flips from "Saved" to
     *  "Continue to {title} →" and clicking it advances the hub to
     *  that subsection. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    // Form state lives in the hub-scoped form-state context so selections
    // and the lastSubmitted snapshot survive navigation. Server hydration
    // is handled once inside OnboardingFormStateProvider, so there's no
    // per-mount hydration effect here anymore.
    const [state, setState] = useUseCaseFormState();
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { useCases, capitalRange, distribution, lastSubmitted } = state;

    // Yield distribution is only collected for the Neobank use case
    // (the picker is hidden otherwise) and the backend only requires
    // `yieldDistribution` in the payload when `useCases` includes
    // neobank. Mirror that condition here so a Treasury-only applicant
    // can save without picking a distribution.
    const distributionRequired = useCases.has("neobank-d2u");
    const submittable =
        useCases.size > 0 &&
        capitalRange !== null &&
        (!distributionRequired || distribution !== null);

    const isDirty =
        lastSubmitted !== null &&
        (!setsEqual(useCases, lastSubmitted.useCases) ||
            capitalRange !== lastSubmitted.capitalRange ||
            distribution !== lastSubmitted.distribution);
    const isSavedClean = lastSubmitted !== null && !isDirty;

    const toggleUseCase = (id: UseCaseId): void => {
        const next = new Set(useCases);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setState({ ...state, useCases: next });
    };

    const commit = async (): Promise<void> => {
        if (!submittable) return;
        const payload: UseCaseAndIntentPayload = {
            useCases: Array.from(useCases).map((id) => USE_CASE_TO_BACKEND[id]),
            expectedCapitalRange: CAPITAL_RANGE_TO_BACKEND[capitalRange!],
            ...(useCases.has("neobank-d2u") && distribution
                ? { yieldDistribution: DISTRIBUTION_TO_BACKEND[distribution] }
                : {})
        };

        const finalizeLocal = (): void => {
            setState({
                ...state,
                lastSubmitted: {
                    useCases: new Set(useCases),
                    capitalRange,
                    distribution
                }
            });
            onSubmit(payload);
        };

        if (!context) {
            // Dev/mock path — no live application, just update local state
            // so the rendering layer can react to the "Saved" toggle.
            finalizeLocal();
            return;
        }

        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await submitUseCaseAndIntent(
                context.application.uuid,
                payload
            );
            context.applyStepUpdate(result);
            finalizeLocal();
        } catch (err) {
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Use case was not saved. Check the selections and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title="Use Case & Intent"
                className="pb-4 border-b border-line-base"
            />

            <FormCard
                title="Use Cases"
                hint="Pick every use case you intend to run."
            >
                <div className="flex flex-wrap gap-1.5">
                    {USE_CASE_OPTIONS.map((opt) => {
                        const selected = useCases.has(opt.value);
                        return (
                            <Button
                                key={opt.value}
                                type="button"
                                variant="toggle"
                                interaction="default"
                                size="sm"
                                aria-selected={selected ? "true" : undefined}
                                onClick={() => toggleUseCase(opt.value)}
                                className="rounded-xs"
                            >
                                <Text variant="small" render={<span />}>
                                    {opt.label}
                                </Text>
                            </Button>
                        );
                    })}
                </div>
                {/* Yield distribution only matters when end users are
                    downstream of the applicant (Neobank). Other use
                    cases don't have an end-user distribution decision
                    to make, so the sub-card stays collapsed. The
                    animated sunken-with-border container makes it
                    read as a Neobank-specific follow-up rather than a
                    standalone section. */}
                <AnimatePresence initial={false}>
                    {useCases.has("neobank-d2u") && (
                        <motion.div
                            key="yield-distribution"
                            initial={{
                                height: 0,
                                opacity: 0,
                                marginTop: 0
                            }}
                            animate={{
                                height: "auto",
                                opacity: 1,
                                marginTop: 16
                            }}
                            exit={{
                                height: 0,
                                opacity: 0,
                                marginTop: 0
                            }}
                            transition={{
                                duration: 0.3,
                                ease: easings.outCubic
                            }}
                            className="overflow-hidden"
                        >
                            <Card surface="sunken" className="px-4 py-4">
                                <Heading
                                    title="Use of Yield"
                                    titleVariant="h3"
                                    className="pb-3"
                                />
                                <SegmentedPicker
                                    options={DISTRIBUTION_OPTIONS}
                                    value={distribution}
                                    onChange={(v) =>
                                        setState({
                                            ...state,
                                            distribution: v
                                        })
                                    }
                                />
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>
            </FormCard>

            <FormCard
                title="Expected Capital Deployment"
                hint="How much capital you plan to deploy."
            >
                <SegmentedPicker
                    options={CAPITAL_RANGE_OPTIONS}
                    value={capitalRange}
                    onChange={(v) => setState({ ...state, capitalRange: v })}
                />
            </FormCard>

            <FormPrimaryAction
                submittable={submittable}
                submitting={submitting}
                isSavedClean={isSavedClean}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
                onSave={() => void commit()}
                error={submitError}
            />
        </motion.div>
    );
}
